import type { UserRole } from '@/types/enums'

export type PermissionKey =
  | 'view_dashboard'
  | 'view_employees'
  | 'create_employee'
  | 'edit_employee'
  /** Çalışan izin hakedişini elle düzeltme — admin ve İK */
  | 'edit_leave_entitlements'
  | 'delete_employee'
  /** TC / IBAN / maaş maskeleme UI'da göz ile açma */
  | 'reveal_sensitive_employee_data'
  | 'view_projects'
  | 'create_project'
  | 'edit_project'
  | 'delete_project'
  /** Çalışanın projesini değiştirme / projeye çalışan ekleme-çıkarma (backend: /employees/:id/projects ve /projects/:id/employees ile aynı roller) */
  | 'change_employee_project'
  | 'view_requests'
  | 'create_request'
  | 'approve_request'
  | 'reject_request'
  | 'view_payroll'
  | 'create_payroll'
  | 'edit_payroll'
  | 'delete_payroll'
  | 'view_payroll_files'
  | 'approve_payroll'
  | 'view_documents'
  | 'upload_document'
  | 'view_reports'
  | 'view_settings'
  | 'edit_settings'
  | 'manage_users'
  | 'view_org_hierarchy'
  | 'edit_org_hierarchy'
  /** KVKK erişim / değişiklik audit kayıtları */
  | 'view_audit_logs'
  /** Envanter & KKD görüntüleme */
  | 'view_inventory'
  /** Envanter & KKD değiştirme (yalnız Satın Alma) */
  | 'manage_inventory'
  | 'view_procurement_requests'
  /** TÜM satın alma taleplerini görme (Satın Alma, Şirket Müdürü, Admin) */
  | 'view_all_procurement_requests'
  | 'create_procurement_request'
  /** Satın alma talebi onay/red (yalnız Satın Alma) */
  | 'approve_procurement_request'
  /** Disiplin kayıtları (yalnız İK / İK Müdürü) */
  | 'manage_discipline'
  /** Çalışanlara manuel bildirim gönderme */
  | 'send_notification'

export const ROLE_PERMISSIONS: Record<UserRole, PermissionKey[]> = {
  SUPER_ADMIN: [
    'view_dashboard',
    'view_employees', 'create_employee', 'edit_employee', 'edit_leave_entitlements', 'delete_employee', 'reveal_sensitive_employee_data',
    'view_projects', 'create_project', 'edit_project', 'delete_project',
    'change_employee_project',
    'view_requests', 'create_request', 'approve_request', 'reject_request',
    'view_payroll', 'create_payroll', 'edit_payroll', 'delete_payroll', 'view_payroll_files', 'approve_payroll',
    'view_documents', 'upload_document',
    'view_reports',
    'view_settings', 'edit_settings',
    'manage_users',
    'view_org_hierarchy', 'edit_org_hierarchy',
    'view_audit_logs',
    'view_inventory', 'manage_inventory',
    'view_procurement_requests', 'view_all_procurement_requests', 'create_procurement_request', 'approve_procurement_request',
    'manage_discipline', 'send_notification'
  ],
  ADMIN: [
    'view_dashboard',
    'view_employees', 'create_employee', 'edit_employee', 'edit_leave_entitlements', 'delete_employee', 'reveal_sensitive_employee_data',
    'view_projects', 'create_project', 'edit_project', 'delete_project',
    'change_employee_project',
    'view_requests', 'create_request', 'approve_request', 'reject_request',
    'view_payroll', 'create_payroll', 'edit_payroll', 'delete_payroll', 'view_payroll_files', 'approve_payroll',
    'view_documents', 'upload_document',
    'view_reports',
    'view_settings', 'edit_settings',
    'manage_users',
    'view_org_hierarchy', 'edit_org_hierarchy',
    'view_audit_logs',
    'view_inventory', 'manage_inventory',
    'view_procurement_requests', 'view_all_procurement_requests', 'create_procurement_request', 'approve_procurement_request',
    'manage_discipline', 'send_notification'
  ],
  COMPANY_DIRECTOR: [
    'view_dashboard',
    'view_employees', 'create_employee', 'edit_employee', 'delete_employee', 'reveal_sensitive_employee_data',
    'view_projects', 'edit_project', 'delete_project',
    'change_employee_project',
    'view_requests', 'create_request', 'approve_request', 'reject_request',
    'view_payroll', 'create_payroll', 'edit_payroll', 'view_payroll_files', 'approve_payroll',
    'view_documents', 'upload_document',
    'view_reports',
    'view_settings', 'edit_settings',
    'manage_users',
    'view_org_hierarchy', 'edit_org_hierarchy',
    'view_audit_logs',
    'view_inventory', 'view_procurement_requests', 'view_all_procurement_requests', 'create_procurement_request'
  ],
  EXECUTIVE: [
    'view_dashboard',
    'view_employees', 'create_employee', 'edit_employee', 'reveal_sensitive_employee_data',
    'view_projects', 'edit_project', 'delete_project',
    'change_employee_project',
    'view_requests', 'create_request', 'approve_request', 'reject_request',
    'view_payroll', 'view_payroll_files',
    'view_documents', 'upload_document',
    'view_reports',
    'view_settings', 'edit_settings',
    'manage_users',
    'view_org_hierarchy', 'edit_org_hierarchy',
    'view_audit_logs',
    'view_inventory', 'view_procurement_requests', 'create_procurement_request'
  ],
  HR: [
    'view_dashboard',
    'view_employees', 'create_employee', 'edit_employee', 'edit_leave_entitlements', 'reveal_sensitive_employee_data',
    'view_projects', 'create_project', 'edit_project', 'delete_project',
    'change_employee_project',
    'view_requests', 'create_request',
    'view_payroll', 'view_payroll_files',
    'view_documents', 'upload_document',
    'view_reports',
    'view_settings',
    'manage_users',
    'view_org_hierarchy', 'edit_org_hierarchy',
    'view_audit_logs',
    'view_inventory', 'view_procurement_requests', 'create_procurement_request',
    'manage_discipline'
  ],
  HR_MANAGER: [
    'view_dashboard',
    'view_employees', 'create_employee', 'edit_employee', 'edit_leave_entitlements', 'reveal_sensitive_employee_data',
    'view_projects', 'create_project', 'edit_project', 'delete_project',
    'change_employee_project',
    'view_requests', 'create_request', 'approve_request', 'reject_request',
    'view_payroll', 'view_payroll_files',
    'view_documents', 'upload_document',
    'view_reports',
    'view_settings',
    'manage_users',
    'view_org_hierarchy', 'edit_org_hierarchy',
    'view_audit_logs',
    'view_inventory', 'view_procurement_requests', 'create_procurement_request',
    'manage_discipline', 'send_notification'
  ],
  FINANCE: [
    'view_dashboard',
    'view_employees', 'create_employee', 'edit_employee', 'reveal_sensitive_employee_data',
    'view_projects',
    'view_requests',
    'view_payroll', 'view_payroll_files',
    'view_documents',
    'view_reports',
    'view_org_hierarchy',
    'view_audit_logs',
    'view_inventory', 'view_procurement_requests', 'create_procurement_request',
    'send_notification'
  ],
  OPERATIONS: [
    'view_dashboard',
    'view_employees', 'create_employee', 'edit_employee', 'reveal_sensitive_employee_data',
    'view_projects', 'edit_project',
    'change_employee_project',
    'view_requests',
    'view_payroll', 'view_payroll_files',
    'view_documents',
    'view_reports',
    'view_org_hierarchy',
    'view_audit_logs',
    'view_inventory', 'view_procurement_requests', 'create_procurement_request'
  ],
  MANAGER: [
    'view_dashboard',
    'view_employees', 'create_employee', 'edit_employee', 'reveal_sensitive_employee_data',
    'view_projects', 'edit_project',
    'change_employee_project',
    'view_requests', 'approve_request', 'reject_request',
    'view_payroll', 'view_payroll_files',
    'view_documents',
    'view_reports',
    'view_org_hierarchy',
    'view_audit_logs',
    'view_inventory', 'view_procurement_requests', 'view_all_procurement_requests', 'create_procurement_request',
    'send_notification'
  ],
  PROCUREMENT_ADMIN_AFFAIRS: [
    'view_dashboard',
    'view_employees', 'create_employee', 'edit_employee', 'reveal_sensitive_employee_data',
    'view_requests',
    'view_payroll', 'view_payroll_files',
    'view_org_hierarchy',
    'view_inventory', 'manage_inventory',
    'view_procurement_requests', 'view_all_procurement_requests', 'create_procurement_request', 'approve_procurement_request',
    'send_notification'
  ],
  EMPLOYEE: [
    'view_dashboard',
    'view_requests', 'create_request',
    'view_documents',
    'view_payroll', 'view_payroll_files',
    'view_org_hierarchy',
    'view_audit_logs',
    'create_procurement_request'
  ]
}
